/**
 * @file     flowKitTokens.ts
 * @readme   ../README.md
 * @purpose  Static design-token references for the step-flow kit. Colours are `var(--r-*)` refs, so the kit follows the boot-time theme without reading the theme context, renders the same on server and client, and works in tests that stub the theme hook.
 */
import { layoutTokens } from '@/design-system/tokens/layout';
import { motionTokens } from '@/design-system/tokens/motion';
import {
  SEMANTIC_CSS_VARS,
  semanticRoleColors,
} from '@/design-system/tokens/semanticColors';
import type { SemanticColorTokens } from '@/core/types';

const semanticVars = Object.fromEntries(
  Object.entries(SEMANTIC_CSS_VARS).map(([key, cssVar]) => [key, `var(${cssVar})`]),
) as Record<keyof SemanticColorTokens, string>;

export const flowColors = { ...semanticVars, ...semanticRoleColors };

export const flowRadius = layoutTokens.radius;

const { durations, easing } = motionTokens;

export const flowTransition = (...properties: string[]): string =>
  properties.map((property) => `${property} ${durations.fast}ms ${easing.standard}`).join(', ');

export const flowMotion = motionTokens;

export const reducedMotion = '@media (prefers-reduced-motion: reduce)';

/** Visible keyboard focus shared by every kit control. */
export const flowFocusRing = {
  '&:focus-visible': {
    outline: `2px solid ${semanticVars.interactiveFocusRing}`,
    outlineOffset: '2px',
  },
} as const;

/** Screen-reader-only text that still sits in the accessible name. */
export const visuallyHidden = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
} as const;

/** The step flow's primary action: 48px, control radius, flat. */
export const flowPrimaryButtonSx = {
  minHeight: layoutTokens.button.md.minHeight,
  px: `${layoutTokens.button.md.paddingX}px`,
  borderRadius: `${layoutTokens.radius.md}px`,
  textTransform: 'none',
  fontSize: 16,
  fontWeight: 500,
  boxShadow: 'none',
  '&:hover': { boxShadow: 'none' },
} as const;
